import { Box } from '@mui/material'

interface TextPart {
  text: string
  bold: boolean
  underline: boolean
}

function parseFormattedText(text: string): TextPart[] {
  const parts: TextPart[] = []
  const regex = /(\*\*(.+?)\*\*)|(__(.+?)__)/g

  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({
        text: text.substring(lastIndex, match.index),
        bold: false,
        underline: false,
      })
    }

    if (match[1]) {
      parts.push({ text: match[2], bold: true, underline: false })
    } else if (match[3]) {
      parts.push({ text: match[4], bold: false, underline: true })
    }

    lastIndex = match.index + match[0].length
  }

  if (lastIndex < text.length) {
    parts.push({ text: text.substring(lastIndex), bold: false, underline: false })
  }

  if (parts.length === 0) {
    parts.push({ text: '', bold: false, underline: false })
  }

  return parts
}

const FormattedText = ({ text }: { text: string }) => {
  const parts = parseFormattedText(text)

  return (
    <>
      {parts.map((part, index) => {
        if (part.bold && part.underline) {
          return (
            <strong key={index}>
              <span style={{ textDecoration: 'underline' }}>{part.text}</span>
            </strong>
          )
        }
        if (part.bold) {
          return <strong key={index}>{part.text}</strong>
        }
        if (part.underline) {
          return (
            <span key={index} style={{ textDecoration: 'underline' }}>
              {part.text}
            </span>
          )
        }
        return <span key={index}>{part.text}</span>
      })}
    </>
  )
}

const PlainTextPreview = ({ content }: { content: string }) => {
  const paragraphs = content.split(/\n{2,}/)

  return (
    <Box>
      {paragraphs.map((paragraph, pIndex) => (
        <Box key={pIndex} sx={{ mb: 1.5 }}>
          {paragraph.split('\n').map((line, lIndex) => (
            <Box key={lIndex} component='span'>
              {lIndex > 0 && <br />}
              <FormattedText text={line} />
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  )
}

interface LegalDocumentPreviewProps {
  content: string
}

export const LegalDocumentPreview = ({ content }: LegalDocumentPreviewProps) => {
  if (!content) return null

  const isHtml = /<[a-z][\s\S]*>/i.test(content)

  if (isHtml) {
    return (
      <Box
        sx={{
          '& a': { color: 'primary.main' },
          '& h1, & h2, & h3': { mt: 2, mb: 1 },
          '& p': { mb: 1 },
          '& ul, & ol': { pl: 3 },
        }}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    )
  }

  return <PlainTextPreview content={content} />
}
